import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import {
  Button,
  Callout,
  Dialog,
  Flex,
  Form,
  MenuLinkButton,
  PasswordInput,
  ServerErrorCallout,
  Text,
  TextArea,
} from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { useAuth } from '@/hooks/useAuth';
import { analyticsClient } from '@/lib';
import { isAuthError } from '@/lib/auth';
import { gdprService } from '@/services';
import { fieldLabelStyle } from '@/views/auth/styles';

export function DeleteAccountComponent() {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { currentUser: profile } = useCurrentUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [hasPending, setHasPending] = useState(false);
  const [checkingPending, setCheckingPending] = useState(true);
  const hasPasswordProvider = user?.providerData.some(p => p.providerId === 'password') ?? false;

  const reauthenticate = async () => {
    if (!user) return;
    if (hasPasswordProvider) {
      const email = user.email ?? profile?.email;
      if (!email) {
        throw new Error(t('account.deleteAccount.error'));
      }
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(user, credential);
      return;
    }
    const providerId = user.providerData.find(p => p.providerId !== 'password')?.providerId;
    if (providerId === 'google.com') {
      await reauthenticateWithPopup(user, new GoogleAuthProvider());
    } else if (providerId === 'apple.com') {
      await reauthenticateWithPopup(user, new OAuthProvider('apple.com'));
    } else {
      throw new Error(t('account.deleteAccount.socialNotSupported'));
    }
  };

  const reportDeleteError = (err: unknown) => {
    const isPasswordError =
      isAuthError(err) &&
      (err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/invalid-login-credentials');
    if (isPasswordError) {
      setDeleteError(t('account.deleteAccount.passwordError'));
    } else {
      setDeleteError(err instanceof Error ? err.message : t('account.deleteAccount.error'));
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    if (hasPasswordProvider && !password) return;

    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await reauthenticate();
      await gdprService.requestAccountDeletion(reason);
      analyticsClient.capture('account_deletion_requested');
      toast.success(t('account.deleteAccount.sent'));
      setHasPending(true);
      setPassword('');
      setReason('');
      setConfirmOpen(false);
    } catch (err) {
      console.error('Failed to request account deletion:', err);
      reportDeleteError(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const check = async () => {
      const pending = await gdprService.hasPendingDeletion(user.uid);
      if (!cancelled) {
        setHasPending(pending);
        setCheckingPending(false);
      }
    };
    void check();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
      <Dialog.Trigger>
        <MenuLinkButton
          destructive
          title={t('account.deleteAccount.title')}
          subtitle={t('account.deleteAccount.subtitle')}
        />
      </Dialog.Trigger>
      <Dialog.Content size="1" maxWidth="320px">
        <Flex direction="column" gap="3" py="2">
          <Dialog.Title align="center" size="3" weight="medium" mb="0">
            {t('account.deleteAccount.confirmTitle')}
          </Dialog.Title>
          <Dialog.Description align="center" size="1" color="gray" mb="0">
            {hasPasswordProvider
              ? t('account.deleteAccount.confirmBody')
              : t('account.deleteAccount.socialConfirmBody')}
          </Dialog.Description>
          {hasPasswordProvider && (
            <Form.Root onSubmit={e => e.preventDefault()} noValidate>
              <Form.Field name="password">
                <PasswordInput
                  showStrengthCheck={false}
                  minLength={1}
                  label={t('account.deleteAccount.passwordLabel')}
                  placeholder={t('account.deleteAccount.passwordPlaceholder')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </Form.Field>
            </Form.Root>
          )}
          <Flex direction="column" gap="1" width="100%">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('account.deleteAccount.reasonLabel')}
            </Text>
            <TextArea
              id="delete-account-reason"
              rows={3}
              maxLength={500}
              placeholder={t('account.deleteAccount.reasonPlaceholder')}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </Flex>
          {hasPending && <Callout>{t('account.deleteAccount.pending')}</Callout>}
          {deleteError && <ServerErrorCallout message={deleteError} />}
          <Flex direction="column" gap="2" width="100%" mt="2">
            <Button
              type="button"
              color="crimson"
              highContrast
              fullWidth
              disabled={
                !user ||
                (hasPasswordProvider ? !password : false) ||
                deleteLoading ||
                hasPending ||
                checkingPending
              }
              isLoading={deleteLoading}
              onClick={() => void handleDeleteAccount()}
            >
              {t('account.deleteAccount.confirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              color="gray"
              fullWidth
              disabled={deleteLoading}
              onClick={() => setConfirmOpen(false)}
            >
              {t('account.deleteAccount.cancel')}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
